import React from "react";
import Loading from "./Loading";
import { getApiUrl } from '../core/config/api';

/**
 * Inline PDF preview.
 *
 * `<object type="application/pdf">` renders inline on desktop browsers, but on
 * iOS Safari and most mobile browsers it paints BLANK — and, crucially, it does
 * not fall through to the child `<p>` (the browser "accepts" the type, then
 * shows nothing), so the reader is left staring at an empty box with no way to
 * open the file. Two things fix that without a heavyweight pdf.js dependency:
 *
 *   - an always-visible "Open in new tab" action ABOVE the embed, so the
 *     document is one tap away no matter what the embed does; and
 *   - `embedded` mode, which drops the fixed-800px lookbook chrome for a
 *     responsive frame (used by the CONTRACTS section, which appears in a
 *     narrow accordion on both desktop and mobile).
 *
 * Default rendering is unchanged for the lookbook / Invoice / agreement callers.
 */
const PdfShower = ({ fileId, embedded = false, label = "PDF" }) => {
  const pdfUrl = fileId
  ? !isNaN(fileId)
    ? getApiUrl(`/api/pdfs/${fileId}`)
    : (fileId.startsWith('blob:') || fileId.startsWith('http'))
      ? fileId
      : getApiUrl(fileId)
  : null;

  if (!pdfUrl) {
    return (
      <div className={embedded ? "rc-doc-preview" : "lookbook-preview"}>
        <div className={embedded ? "rc-doc-preview__frame" : "bbb"}>
          <Loading text="Please wait while the PDF is prepared..." type="loading" />
        </div>
      </div>
    );
  }

  const openLink = (
    <a
      className="rc-doc-preview__open"
      href={pdfUrl}
      target="_blank"
      rel="noopener noreferrer"
    >
      Open {label} in new tab
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5"
          stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </a>
  );

  if (embedded) {
    // Responsive frame + an always-available open action. The embed still
    // renders inline where the browser supports it (desktop); where it does
    // not (mobile), the open link is the reliable path and the frame collapses
    // to its min-height rather than a blank 800px void.
    return (
      <div className="rc-doc-preview">
        <div className="rc-doc-preview__bar">{openLink}</div>
        <div className="rc-doc-preview__frame">
          <object data={pdfUrl} type="application/pdf" width="100%" height="100%">
            <div className="rc-doc-preview__fallback">
              <p>Preview isn’t available on this device.</p>
              {openLink}
            </div>
          </object>
        </div>
      </div>
    );
  }

  return (
    <div className="lookbook-preview">
      <div className="bbb">
        <div id="pdfContainer">
          <object data={pdfUrl} type="application/pdf" width="100%" height="100%">
            <p>
              Your browser does not support PDFs.{" "}
              <a href={pdfUrl} target="_blank" rel="noopener noreferrer">
                Click here to download
              </a>
              .
            </p>
          </object>
        </div>
      </div>
    </div>
  );
};

export default PdfShower;
